import React, { useEffect, useRef } from 'react';

const Map = ({ center, markers, route, onMapClick, zoom = 13 }) => {
  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const markersRef = useRef([]);
  const routePolylineRef = useRef(null);

  useEffect(() => {
    if (!window.google) {
      console.error('Google Maps API not loaded');
      return;
    }

    // Initialize map
    googleMapRef.current = new window.google.maps.Map(mapRef.current, {
      center: center || { lat: 0, lng: 0 },
      zoom: zoom,
      mapTypeControl: true,
      streetViewControl: false
    });

    // Add click listener
    if (onMapClick) {
      googleMapRef.current.addListener('click', (e) => {
        onMapClick({
          lat: e.latLng.lat(),
          lng: e.latLng.lng()
        });
      });
    }
    // Run once on mount to initialize the map; center/zoom have dedicated update
    // effects below and onMapClick is bound a single time by design.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update center
  useEffect(() => {
    if (googleMapRef.current && center) {
      googleMapRef.current.setCenter(center);
    }
  }, [center]);

  // Update markers
  useEffect(() => {
    if (!googleMapRef.current) return;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    // Add new markers
    if (markers && markers.length > 0) {
      markers.forEach((markerData) => {
        const marker = new window.google.maps.Marker({
          position: markerData.position,
          map: googleMapRef.current,
          title: markerData.title,
          icon: markerData.icon || null,
          label: markerData.label || null
        });

        if (markerData.infoWindow) {
          const infoWindow = new window.google.maps.InfoWindow({
            content: markerData.infoWindow
          });
          marker.addListener('click', () => {
            infoWindow.open(googleMapRef.current, marker);
          });
        }

        markersRef.current.push(marker);
      });

      // Fit bounds to show all markers
      if (markers.length > 1) {
        const bounds = new window.google.maps.LatLngBounds();
        markers.forEach(marker => bounds.extend(marker.position));
        googleMapRef.current.fitBounds(bounds);
      }
    }
  }, [markers]);

  // Update route
  useEffect(() => {
    if (!googleMapRef.current) return;

    // Clear existing route
    if (routePolylineRef.current) {
      routePolylineRef.current.setMap(null);
    }

    // Draw new route
    if (route && route.length > 0) {
      routePolylineRef.current = new window.google.maps.Polyline({
        path: route,
        geodesic: true,
        strokeColor: '#2563eb',
        strokeOpacity: 0.8,
        strokeWeight: 4,
        map: googleMapRef.current
      });
    }
  }, [route]);

  return (
    <div
      ref={mapRef}
      style={{
        width: '100%',
        height: '100%',
        minHeight: '400px'
      }}
    />
  );
};

export default Map;
